import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import StatusMenu, {
  StatusItemProps,
  StatusItemType,
} from '@components/StatusMenu';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';

import * as S from './styles';

export interface GroupedStatusItem {
  label: string;
  value: string;
  total: number;
  type: StatusItemType;
  color?: string;
  children?: StatusItemProps[];
}

interface GroupedStatusMenuProps {
  data: GroupedStatusItem[];
  onChange: (item: StatusItemProps | undefined) => void;
  itemActive?: string;
  actionActive?: string;
  showIconClear?: boolean;
}

const GroupedStatusMenu = ({
  data,
  onChange,
  itemActive,
  actionActive,
  showIconClear,
}: GroupedStatusMenuProps) => {
  const { t } = useTranslation('main');
  const [showDetails, setShowDetails] = useState(false);
  const [detailsList, setDetailsList] = useState<StatusItemProps[]>();
  const [crrActionActive, setCrrActionActive] = useState<string>();

  const showErrorMessage = useMemo(() => {
    const isError =
      data?.find(item => item.value === itemActive)?.type === 'error';
    return isError;
  }, [data, itemActive]);

  useEffect(() => {
    if (actionActive) {
      setCrrActionActive(actionActive);
    }
  }, [actionActive, showDetails]);

  useEffect(() => {
    if (actionActive !== crrActionActive && showDetails) {
      setShowDetails(false);
    }
  }, [actionActive, crrActionActive, showDetails]);

  const getColorByType = useCallback((type: StatusItemType) => {
    switch (type) {
      case 'success':
        return 'var(--primary-color)';
      case 'processing':
        return '#00afef';
      default:
        return '#ff4d4f';
    }
  }, []);

  const fixedOrder = useMemo(() => ['success', 'processing', 'error'], []);

  const verifyByOrder = useCallback(
    (itemA: GroupedStatusItem, itemB: GroupedStatusItem) => {
      const indexA = fixedOrder.indexOf(itemA.type);
      const indexB = fixedOrder.indexOf(itemB.type);

      if (indexA < indexB) {
        return -1;
      }
      if (indexA > indexB) {
        return 1;
      }
      return 0;
    },
    [fixedOrder],
  );

  const groupedData = useMemo((): GroupedStatusItem[] | undefined => {
    if (!data) return undefined;

    const groupedResult: Record<string, GroupedStatusItem> = {};

    data.forEach(item => {
      const { type, value, total } = item;
      const color = getColorByType(type);

      if (!groupedResult[type]) {
        groupedResult[type] = {
          type,
          label: type,
          value,
          total: 0,
          color,
          children: [],
        };
      }

      groupedResult[type].total += total;
      groupedResult[type].children?.push({ ...item, color });
    });

    const result = Object.values(groupedResult);

    result.sort(verifyByOrder);

    return result;
  }, [data, getColorByType, verifyByOrder]);

  const dataTreated = useMemo(() => {
    return groupedData?.map(item => ({
      ...item,
      label: t(`grouped-status-menu.${item.label}`, {
        count: item.total,
      }),
      value: item.children?.[0]?.value ?? '',
    }));
  }, [groupedData, t]);

  const handleSelectedCardDetails = useCallback(
    (item: StatusItemProps) => {
      onChange(item.value !== itemActive ? item : undefined);
    },
    [itemActive, onChange],
  );

  const handleSelectedCard = useCallback(
    (item: StatusItemProps) => {
      const childrenLength = (item?.children && item?.children?.length) ?? 0;
      if (item.value === itemActive && childrenLength < 2) {
        onChange(undefined);
        setDetailsList(undefined);
      } else if (childrenLength > 1) {
        setDetailsList(item.children);
        setShowDetails(true);
      } else {
        setDetailsList(undefined);
        onChange(item);
      }
    },
    [itemActive, onChange],
  );

  const handleResetSubStatusWhenReturn = useCallback(() => {
    const isSubStatusActive = !!detailsList?.find(
      item => item.value === itemActive,
    );

    if (isSubStatusActive) {
      onChange(undefined);
    }
  }, [detailsList, itemActive, onChange]);

  return (
    <S.Container>
      {showDetails ? (
        <>
          <Button
            icon={<LeftOutlined />}
            onClick={() => {
              handleResetSubStatusWhenReturn();
              setShowDetails(false);
            }}
            className="grouped-status-menu-return-button"
          />
          <StatusMenu
            size="small"
            layout="horizontal"
            data={detailsList}
            handleSelectedCard={handleSelectedCardDetails}
            itemActive={itemActive}
            style={{ marginLeft: 40 }}
            showIconClear={showIconClear}
          />
        </>
      ) : (
        <StatusMenu
          size="small"
          layout="horizontal"
          data={dataTreated}
          handleSelectedCard={handleSelectedCard}
          itemActive={itemActive}
          showIconClear={showIconClear}
        />
      )}
      {showErrorMessage && (
        <Alert
          showIcon
          type="error"
          message={t('contactSupport')}
          style={{ marginTop: 8, fontSize: '0.9rem' }}
        />
      )}
    </S.Container>
  );
};

export default GroupedStatusMenu;
